import { type Entity, generateEntityId } from './entity';
import type { CollisionMask } from '../sim/collisionMask';
import { WA_PHYSICS, type Vector2 } from '../core/constants';
import type { Worm } from './worm';

export type ProjectileType =
  | 'grenade'
  | 'bazooka'
  | 'cluster'
  | 'clusterlet'
  | 'mortar'
  | 'dynamite'
  | 'holyGrenade'
  | 'banana'
  | 'bananalet'
  | 'molotov'
  | 'napalm'
  | 'grub'
  | 'grubSS'
  | 'aduka'
  | 'nak'
  | 'adukaSS'
  | 'lightningT1'
  | 'lightningT2'
  | 'knight'
  | 'trico'
  | 'kalsiddon'
  | 'jdT2'
  | 'jdSS';

export interface ProjectileConfig {
  x: number;
  y: number;
  vx: number;
  vy: number;
  type: ProjectileType;
  timer?: number;
  bounceH?: number;
  bounceV?: number;
  friction?: number;
  windAffected?: boolean;
  explosionRadius?: number;
  maxDamage?: number;
  ownerId: string;
  /** Explode without cratering terrain (legacy Aduka path-preserve flag). */
  skipTerrainOnExplode?: boolean;
  /** Shared impact lock for Aduka tracers: the first tracer records its impact
   *  point here; the rest explode at that exact point (regardless of the
   *  craters the earlier shots dug), so all three land together AND each one
   *  destroys terrain. */
  impactLock?: { x: number | null; y: number | null } | null;
  /** Grub SS: the ball passes through tanks (no collision) and electrocutes
   *  them with damage-over-time instead. */
  pierceWorms?: boolean;
  /** Grub SS: fuse (seconds) that only starts once the ball first hits
   *  terrain. -1 (default) keeps the launch-time `timer` behaviour. */
  landingFuse?: number;
  /** Aduka SS: ignore terrain entirely (the shot phases straight through it). */
  phaseTerrain?: boolean;
  /** Trico T2: orbit direction for an outer ball (0 = center, no orbit). The
   *  two outer balls spin around the shared ballistic center in flight. */
  orbitDir?: number;
  /** Trico T2: initial orbit phase (radians) for an outer ball. */
  orbitAngle?: number;
  /** Tracer / sub-projectile weapons (aduka, lightning, knight, kalsiddon):
   *  no on-contact explosion (no crater, no contact damage) - only the
   *  follow-up payload (Thor strike / bolt / swords / homing missiles) hurts.
   *  The onExplode callback (payload trigger) still fires. */
  noContactExplosion?: boolean;
}

export class Projectile implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2;
  active = true;

  type: ProjectileType;
  timer: number;
  bounceH: number;
  bounceV: number;
  friction: number;
  windAffected: boolean;
  explosionRadius: number;
  maxDamage: number;
  ownerId: string;
  rotation = 0;
  bounceCount = 0;
  isOnGround = false;
  skipTerrainOnExplode: boolean;
  impactLock: { x: number | null; y: number | null } | null;
  pierceWorms: boolean;
  landingFuse: number;
  phaseTerrain: boolean;
  /** Trico T2: 0 = center ball; +/-1 = an outer ball orbiting the center. */
  orbitDir: number;
  private orbitAngle: number;
  private orbitOffX = 0;
  private orbitOffY = 0;
  /** Nak T2: true while the shell is tunnelling underground. */
  burrowing = false;
  /** Grub SS: fractional damage-over-time carry, flushed as whole points. */
  dotAccumulator = 0;
  /** Aduka SS: ids of tanks already hit, so each is burst exactly once. */
  piercedWorms = new Set<string>();
  /** Tracer/sub-projectile weapons: skip the on-contact explosion entirely. */
  noContactExplosion: boolean;

  onExplode?: (x: number, y: number) => void;
  onDirectHit?: (worm: Worm, x: number, y: number) => void;
  onPreExplode?: () => void;
  onBounce?: () => void;
  /** Kalsiddon: fires once at the apex of the arc (vy crosses 0, going down). */
  onApexSplit?: (x: number, y: number) => void;
  private apexSplit = false;

  private preExplodeDelay = 0;
  private isPreExploding = false;
  private fuseStarted = false;
  private previousPosition: Vector2;

  constructor(config: ProjectileConfig) {
    this.id = generateEntityId('projectile');
    this.position = { x: config.x, y: config.y };
    this.velocity = { x: config.vx, y: config.vy };
    this.type = config.type;
    this.timer = config.timer ?? -1;
    this.bounceH = config.bounceH ?? 1;
    this.bounceV = config.bounceV ?? 1;
    this.friction = config.friction ?? 0.96;
    this.windAffected = config.windAffected ?? false;
    this.explosionRadius = config.explosionRadius ?? WA_PHYSICS.GRENADE.EXPLOSION_RADIUS;
    this.maxDamage = config.maxDamage ?? WA_PHYSICS.GRENADE.MAX_DAMAGE;
    this.ownerId = config.ownerId;
    this.skipTerrainOnExplode = config.skipTerrainOnExplode ?? false;
    this.impactLock = config.impactLock ?? null;
    this.pierceWorms = config.pierceWorms ?? false;
    this.landingFuse = config.landingFuse ?? -1;
    this.phaseTerrain = config.phaseTerrain ?? false;
    this.orbitDir = config.orbitDir ?? 0;
    this.orbitAngle = config.orbitAngle ?? 0;
    this.noContactExplosion = config.noContactExplosion ?? false;
    this.previousPosition = { x: config.x, y: config.y };
  }

  update(deltaTime: number): void {
    if (!this.active) return;

    this.previousPosition = { x: this.position.x, y: this.position.y };

    if (this.type === 'dynamite') {
      if (!this.isOnGround) {
        this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
        this.position.y += this.velocity.y * deltaTime;
      }
    } else {
      // Captured BEFORE gravity so the apex (vy crossing up->down) is detected.
      const vyBefore = this.velocity.y;
      if (this.type === 'nak' && this.burrowing) {
        // Underground: keep horizontal speed and accelerate UP so the shell
        // curves back out and resurfaces (replaces gravity while buried).
        this.velocity.y -= WA_PHYSICS.NAK.RISE_ACCEL * deltaTime;
      } else {
        this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
      }
      // Trico T2 orbit: outer balls strip last tick's offset, integrate the
      // shared ballistic center, then re-apply a freshly-spun offset - so they
      // orbit the center in flight without the offset drifting/accumulating.
      if (this.orbitDir !== 0) {
        this.position.x -= this.orbitOffX;
        this.position.y -= this.orbitOffY;
      }
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
      // Kalsiddon splits at the TOP of its arc (vy crosses from rising to
      // falling). Fires once.
      if (this.type === 'kalsiddon' && !this.apexSplit && vyBefore <= 0 && this.velocity.y > 0) {
        this.apexSplit = true;
        this.onApexSplit?.(this.position.x, this.position.y);
      }
      if (this.orbitDir !== 0) {
        this.orbitAngle += WA_PHYSICS.TRICO.ORBIT_SPIN * deltaTime;
        this.orbitOffX = Math.cos(this.orbitAngle) * WA_PHYSICS.TRICO.ORBIT_RADIUS;
        this.orbitOffY = Math.sin(this.orbitAngle) * WA_PHYSICS.TRICO.ORBIT_RADIUS;
        this.position.x += this.orbitOffX;
        this.position.y += this.orbitOffY;
      }

      if (
        this.type === 'bazooka' ||
        this.type === 'mortar' ||
        this.type === 'nak' ||
        this.type === 'adukaSS'
      ) {
        this.rotation = Math.atan2(this.velocity.y, this.velocity.x) + Math.PI / 2;
      } else if (
        this.type === 'grenade' ||
        this.type === 'cluster' ||
        this.type === 'holyGrenade' ||
        this.type === 'banana'
      ) {
        this.rotation += 2 * deltaTime;
      } else if (this.type === 'clusterlet' || this.type === 'bananalet') {
        this.rotation += 4 * deltaTime;
      }
    }

    if (this.isPreExploding) {
      this.preExplodeDelay -= deltaTime;
      if (this.preExplodeDelay <= 0) {
        this.isPreExploding = false;
        this.doExplode();
      }
      return;
    }

    if (this.timer > 0) {
      this.timer -= deltaTime;
      if (this.timer <= 0) {
        this.explode();
      }
    }
  }

  applyWind(wind: number, deltaTime: number, windY = 0): void {
    if (this.windAffected) {
      this.velocity.x += wind * 10 * deltaTime;
      if (windY !== 0) {
        this.velocity.y += windY * 10 * deltaTime;
      }
    }
  }

  checkTerrainCollision(terrain: CollisionMask): boolean {
    if (!this.active) return false;

    // Check current position
    if (terrain.isSolid(this.position.x, this.position.y)) {
      return true;
    }

    // Raycast from previous to current position (Bresenham line algorithm)
    const x0 = Math.floor(this.previousPosition.x);
    const y0 = Math.floor(this.previousPosition.y);
    const x1 = Math.floor(this.position.x);
    const y1 = Math.floor(this.position.y);

    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;

    let x = x0;
    let y = y0;

    while (true) {
      if (terrain.isSolid(x, y)) {
        // Collision detected along the path
        // Move projectile back to collision point
        this.position.x = x;
        this.position.y = y;
        return true;
      }

      if (x === x1 && y === y1) break;

      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }

    return false;
  }

  handleTerrainHit(terrain: CollisionMask): void {
    // Grub SS: its fuse only begins once the ball first touches terrain.
    if (this.type === 'grubSS' && !this.fuseStarted && this.landingFuse > 0) {
      this.timer = this.landingFuse;
      this.fuseStarted = true;
    }

    // Nak T2: burrows into the ground instead of bouncing or exploding, but
    // digs in with reduced force (it does not keep its full impact momentum).
    if (this.type === 'nak') {
      if (!this.burrowing) {
        this.burrowing = true;
        this.timer = WA_PHYSICS.NAK.MAX_BURROW_TIME;
        this.velocity.x *= WA_PHYSICS.NAK.BURROW_FORCE_MULTIPLIER;
        this.velocity.y *= WA_PHYSICS.NAK.BURROW_FORCE_MULTIPLIER;
      }
      return;
    }

    if (
      this.type === 'bazooka' ||
      this.type === 'mortar' ||
      this.type === 'clusterlet' ||
      this.type === 'molotov' ||
      this.type === 'napalm' ||
      this.type === 'bananalet' ||
      this.type === 'aduka' ||
      this.type === 'lightningT1' ||
      this.type === 'lightningT2' ||
      this.type === 'knight' ||
      this.type === 'trico' ||
      this.type === 'kalsiddon' ||
      this.type === 'jdT2' ||
      this.type === 'jdSS'
    ) {
      this.explode();
      return;
    }

    if (this.type === 'dynamite') {
      const normal = terrain.getSurfaceNormal(this.position.x, this.position.y, 0, this.velocity.y);
      this.position.x += normal.x * 2;
      this.position.y += normal.y * 2;
      this.velocity.y = 0;
      this.isOnGround = true;
      return;
    }

    const normal = terrain.getSurfaceNormal(
      this.position.x,
      this.position.y,
      this.velocity.x,
      this.velocity.y,
    );

    const dot = this.velocity.x * normal.x + this.velocity.y * normal.y;
    this.velocity.x = (this.velocity.x - 2 * dot * normal.x) * this.bounceH;
    this.velocity.y = (this.velocity.y - 2 * dot * normal.y) * this.bounceV;

    this.position.x += normal.x * 2;
    this.position.y += normal.y * 2;

    this.bounceCount++;
    this.onBounce?.();

    const speed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y);

    if (speed < 30) {
      this.isOnGround = true;
    }

    if (speed < 5) {
      this.velocity.x = 0;
      this.velocity.y = 0;
    }
  }

  applyFriction(): void {
    if (this.isOnGround) {
      this.velocity.x *= this.friction;
      if (Math.abs(this.velocity.x) < 1) {
        this.velocity.x = 0;
      }
    }
  }

  checkGroundBelow(terrain: CollisionMask): void {
    if (!this.isOnGround) return;

    const hasGround =
      terrain.isSolid(this.position.x - 2, this.position.y + 1) ||
      terrain.isSolid(this.position.x, this.position.y + 1) ||
      terrain.isSolid(this.position.x + 2, this.position.y + 1);
    if (!hasGround) {
      this.isOnGround = false;
    }
  }

  checkWaterCollision(waterLevel: number, worldHeight: number): boolean {
    return this.position.y > worldHeight - waterLevel;
  }

  checkWormCollision(worms: Worm[], prevX?: number, prevY?: number): Worm | null {
    for (const worm of worms) {
      if (worm.state === 'dead') continue;
      if (worm.id === this.ownerId && this.bounceCount === 0) continue;

      const box = worm.getBounds();

      if (
        this.position.x >= box.x &&
        this.position.x <= box.x + box.width &&
        this.position.y >= box.y &&
        this.position.y <= box.y + box.height
      ) {
        return worm;
      }

      if (prevX !== undefined && prevY !== undefined) {
        const dx = this.position.x - prevX;
        const dy = this.position.y - prevY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 1) {
          const steps = Math.ceil(dist / 2);
          for (let i = 1; i < steps; i++) {
            const t = i / steps;
            const checkX = prevX + dx * t;
            const checkY = prevY + dy * t;

            if (
              checkX >= box.x &&
              checkX <= box.x + box.width &&
              checkY >= box.y &&
              checkY <= box.y + box.height
            ) {
              this.position.x = checkX;
              this.position.y = checkY;
              return worm;
            }
          }
        }
      }
    }
    return null;
  }

  handleWormHit(worm: Worm): 'explode' | 'bounce' | 'ignore' {
    // Grub balls detonate on a direct worm hit (like a bazooka); they only
    // keep bouncing/rolling and fuse out when they miss everyone.
    if (
      this.type === 'bazooka' ||
      this.type === 'clusterlet' ||
      this.type === 'mortar' ||
      this.type === 'bananalet' ||
      this.type === 'molotov' ||
      this.type === 'napalm' ||
      this.type === 'grub' ||
      this.type === 'aduka' ||
      this.type === 'nak' ||
      this.type === 'lightningT1' ||
      this.type === 'lightningT2' ||
      this.type === 'knight' ||
      this.type === 'trico' ||
      this.type === 'kalsiddon'
    ) {
      this.onDirectHit?.(worm, this.position.x, this.position.y);
      return 'explode';
    }

    if (
      this.type === 'grenade' ||
      this.type === 'cluster' ||
      this.type === 'holyGrenade' ||
      this.type === 'banana'
    ) {
      const box = worm.getBounds();
      const centerX = box.x + box.width / 2;
      const centerY = box.y + box.height / 2;

      let nx = this.position.x - centerX;
      let ny = this.position.y - centerY;
      const len = Math.sqrt(nx * nx + ny * ny);
      if (len > 0) {
        nx /= len;
        ny /= len;
      } else {
        nx = this.velocity.x > 0 ? -1 : 1;
        ny = 0;
      }

      const dot = this.velocity.x * nx + this.velocity.y * ny;
      const bounce = WA_PHYSICS.COLLISION.GRENADE_WORM_BOUNCE;
      this.velocity.x = (this.velocity.x - 2 * dot * nx) * bounce;
      this.velocity.y = (this.velocity.y - 2 * dot * ny) * bounce;

      this.position.x += nx * 3;
      this.position.y += ny * 3;

      this.bounceCount++;
      this.onBounce?.();

      const speed = Math.sqrt(this.velocity.x ** 2 + this.velocity.y ** 2);
      if (speed < 30) {
        this.isOnGround = true;
      }

      return 'bounce';
    }

    return 'ignore';
  }

  explode(): void {
    if (!this.active) return;

    if (this.type === 'holyGrenade' && !this.isPreExploding) {
      this.isPreExploding = true;
      this.preExplodeDelay = 1.5;
      this.velocity.x = 0;
      this.velocity.y = 0;
      this.onPreExplode?.();
      return;
    }

    this.doExplode();
  }

  private doExplode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x, this.position.y);
  }

  destroy(): void {
    this.active = false;
  }

  getRotation(): number {
    return this.rotation;
  }
}
